// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Six routes: the staff, one file, the form, the matrix, what runs out, and the
// catalogue. The matrix and the expiry list are routes of their own rather than
// tabs, because both are screens somebody keeps open while working through
// them — and both are what a home-screen widget links to.

export const routes = [
  {
    path: '/personnel',
    name: 'personnel',
    component: () => import('./pages/PersonnelPage.vue'),
    meta: { permission: 'personnel:read', title: 'personnel.title' , ownsQuery: true /* los filtros van en la consulta: el armazón no la remonta al cambiar */ },
  },
  // Static before the parameter, for the reader: route ranking already handles it.
  {
    path: '/personnel/compliance',
    name: 'personnel-compliance',
    component: () => import('./pages/CompliancePage.vue'),
    meta: { permission: 'personnel:read', title: 'personnel.matrix.title' },
  },
  {
    path: '/personnel/expiring',
    name: 'personnel-expiring',
    component: () => import('./pages/ExpiringPage.vue'),
    // Behind the documents permission, not `read`: knowing what expires is
    // knowing what is on file (§27).
    meta: { permission: 'personnel:documents', title: 'personnel.expiring.title' , ownsQuery: true /* los filtros van en la consulta: el armazón no la remonta al cambiar */ },
  },
  {
    path: '/personnel/catalogue',
    name: 'personnel-catalogue',
    component: () => import('./pages/CataloguePage.vue'),
    meta: { permission: 'personnel:manage', title: 'personnel.catalogue.title' },
  },
  {
    path: '/personnel/new',
    name: 'personnel-new',
    component: () => import('./pages/EmployeeFormPage.vue'),
    meta: { permission: 'personnel:write', title: 'personnel.form.newTitle' },
  },
  {
    path: '/personnel/:id/edit',
    name: 'personnel-edit',
    component: () => import('./pages/EmployeeFormPage.vue'),
    meta: { permission: 'personnel:write', title: 'personnel.form.editTitle' },
  },
  {
    path: '/personnel/:id',
    name: 'personnel-file',
    component: () => import('./pages/EmployeePage.vue'),
    meta: { permission: 'personnel:read', title: 'personnel.detailTitle' },
  },
]

/**
 * One entry, in `business` beside the work.
 *
 * Behind `personnel:read`, which is the staff list — not behind the documents
 * permission. A foreman who needs to know who they have should reach the
 * screen; what they see inside it is another question, and the server answers
 * that one.
 */
export const navigation = [
  {
    to: '/personnel',
    labelKey: 'personnel.title',
    permission: 'personnel:read',
    icon: 'badge',
    group: 'business',
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16), including what this module is called on the PLATFORM's own
// screens — Roles, Settings, Audit and the assistant panel — because a module
// that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    personnel: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        expired: 'un documento caducado | {count} documentos caducados',
        expiring: 'un documento caduca esta semana | {count} documentos caducan esta semana',
      },
      locale: 'es',
      // Los catálogos con los que nace una instancia (backend/domain/catalogues.js).
      // Se guardan en inglés; se traducen solo mientras nadie los haya reescrito.
      defaults: {
        positions: {
          electrician: 'Electricista',
          installer: 'Instalador',
          welder: 'Soldador',
          mechanic: 'Mecánico',
          operative: 'Operario',
          site_foreman: 'Encargado de obra',
          technician: 'Técnico',
          engineering_office: 'Oficina técnica',
          administration: 'Administración',
        },
        categories: {
          labourer: 'Peón',
          semi_skilled: 'Especialista',
          skilled: 'Oficial',
          foreman: 'Encargado',
          technician: 'Técnico',
          engineer: 'Ingeniero',
        },
        activities: {
          work_at_height: 'Trabajos en altura',
          hot_work: 'Trabajos en caliente',
          electrical_risk: 'Riesgo eléctrico',
          confined_spaces: 'Espacios confinados',
          elevating_platform: 'Plataforma elevadora',
          forklift: 'Carretilla elevadora',
          overhead_crane: 'Puente grúa',
          driving: 'Conducción',
        },
      },
      title: 'Control de accesos',
      subtitle: 'Quién está habilitado para entrar en cada emplazamiento u obra, hoy',
      detailTitle: 'Expediente',
      notFound: 'No encontramos este expediente',
      backToList: 'Volver a la plantilla',
      empty: 'Todavía no hay expedientes',
      emptyHint: 'Un expediente es la ficha laboral de alguien: su puesto, sus papeles y para qué está habilitado.',
      noName: 'Sin nombre',
      more: 'Ver más ({shown} de {total})',
      cancel: 'Cancelar',
      save: 'Guardar',
      edit: 'Editar',
      remove: 'Eliminar',

      actions: {
        new: 'Nuevo expediente',
        edit: 'Editar ficha',
        upload: 'Subir documento',
        assign: 'Asignar a obra',
        matrix: 'Matriz de cumplimiento',
        expiring: 'Qué caduca',
        catalogue: 'Requisitos y documentos',
        dossier: 'Generar dossier',
        terminate: 'Dar de baja',
        reinstate: 'Reincorporar',
      },

      dashboard: {
        notCompliant: 'No habilitados',
        notCompliantHelp: 'Cuánta gente en plantilla no cumple ahora mismo algún requisito obligatorio de su puesto.',
        expiring: 'Documentos por caducar',
        expiringHelp: 'Papeles de personal que caducan en los próximos 30 días: reconocimientos, formación, carnés.',
        expired: 'Documentos caducados',
        expiredHelp: 'Papeles de personal que ya han caducado y siguen sin renovar.',
        pendingValidation: 'Por validar',
        pendingValidationHelp: 'Documentos que alguien ha subido y todavía no ha revisado nadie.',
        soonest: 'Lo que caduca antes',
        soonestHelp: 'Los papeles de personal con la caducidad más cercana, de aquí a 60 días.',
      },

      stats: {
        active: 'En plantilla',
        compliant: 'En regla',
        warning: 'Con avisos',
        notCompliant: 'No habilitados',
        notConfigured: 'Sin requisitos',
      },

      windows: {
        expired: 'caducados',
        inDays: 'en {days} días',
        pendingValidation: 'por validar',
      },

      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Nombre, código o número interno',
        position: 'Puesto',
        anyPosition: 'Cualquier puesto',
        department: 'Departamento',
        anyDepartment: 'Cualquier departamento',
        compliance: 'Habilitación',
        anyCompliance: 'Cualquier estado',
        status: 'Situación',
        anyStatus: 'Todos, altas y bajas',
      },

      columns: {
        name: 'Trabajador',
        position: 'Puesto',
        site: 'Centro',
        compliance: 'Habilitación',
        nextExpiry: 'Próxima caducidad',
      },

      status: {
        COMPLIANT: 'Habilitado',
        WARNING: 'Con avisos',
        NON_COMPLIANT: 'No habilitado',
        NOT_CONFIGURED: 'Sin requisitos',
      },

      employmentStatus: { active: 'En alta', inactive: 'De baja' },

      verdict: {
        yes: 'HABILITADO',
        no: 'NO HABILITADO',
        warning: 'HABILITADO CON AVISOS',
        unknown: 'SIN REQUISITOS CONFIGURADOS',
        blocking: '{count} bloqueantes',
        askedBy: '· {rule}',
        noRequirements: 'Nadie ha declarado todavía qué hace falta para este puesto.',
        configure: 'Configurar requisitos',
        nextExpiry: 'Lo primero que vence: {date}',
      },

      // Los códigos que devuelve el motor, en palabras. Que vivan aquí y no en
      // el servidor es lo que permite que el mismo backend hable castellano,
      // catalán, inglés y francés (§16).
      reasons: {
        DOCUMENT_MISSING: 'Falta {document}',
        DOCUMENT_PENDING_VALIDATION: '{document} está subido pero sin validar',
        DOCUMENT_REJECTED: '{document} se rechazó',
        DOCUMENT_EXPIRED: '{document} caducó hace {days} días',
        DOCUMENT_EXPIRING: '{document} caduca en {days} días',
        MEDICAL_FITNESS_MISSING: 'Falta el reconocimiento médico',
        MEDICAL_FITNESS_PENDING: 'El reconocimiento médico está pendiente',
        MEDICAL_FITNESS_EXPIRED: 'El reconocimiento médico caducó hace {days} días',
        MEDICAL_FITNESS_EXPIRING: 'El reconocimiento médico vence en {days} días',
        MEDICAL_FITNESS_UNFIT: 'No apto para el puesto',
        MEDICAL_FITNESS_RESTRICTED: 'Apto con restricciones',
        MEDICAL_FITNESS_NOT_CLEARED: 'El reconocimiento médico no le habilita',
        EMPLOYMENT_ENDED: 'Está de baja en la empresa',
      },

      tabs: {
        documents: 'Papeles',
        fitness: 'Aptitud',
        assignments: 'Obras',
        history: 'Historial',
      },

      file: {
        title: 'Ficha laboral',
        code: 'Código',
        position: 'Puesto',
        category: 'Categoría',
        department: 'Departamento',
        site: 'Centro',
        manager: 'Responsable',
        hiredOn: 'Antigüedad',
        agreement: 'Convenio',
        activities: 'Qué hace',
        noActivities: 'Sin actividades marcadas',
        activitiesHint: 'Las actividades con riesgo deciden qué formación y qué equipos se le exigen.',
        months: '{count} meses',
        years: '{count} años',
        closed: 'Expediente cerrado',
        closedOn: 'Baja el {date}',
      },

      documents: {
        empty: 'Todavía no hay papeles en este expediente',
        noExpiry: 'No caduca',
        versions: '{count} versiones',
        versionsTitle: 'Versiones del documento',
        versionsHint: 'Renovar no pisa la versión anterior: se queda, y así consta con qué papel se entró en una obra.',
        open: 'Abrir',
        validate: 'Validar',
        validated: 'Documento validado',
        reject: 'Rechazar',
        rejectTitle: 'Rechazar el documento',
        rejectHint: 'Motivo — es lo que se le dirá al trabajador al pedírselo otra vez',
        renew: 'Renovar',
        void: 'Anular',
        voidTitle: 'Anular la versión',
        voidHint: 'Motivo — la fila se queda como registro, el fichero se borra',
        columns: {
          type: 'Documento',
          state: 'Estado',
          validUntil: 'Caduca',
          versions: 'Versiones',
          filedBy: 'Subido por',
          validation: 'Validación',
        },
        state: {
          pending: 'Pendiente',
          pending_validation: 'Por validar',
          valid: 'Válido',
          rejected: 'Rechazado',
          archived: 'Archivado',
          expiring: 'Caduca pronto',
          expired: 'Caducado',
        },
        validation: {
          none: 'No requiere',
          pending: 'Por validar',
          validated: 'Validado',
          rejected: 'Rechazado',
        },
      },

      fitness: {
        audited: 'Este apartado es confidencial. Cada consulta queda registrada en la auditoría.',
        record: 'Registrar reconocimiento',
        recorded: 'Reconocimiento registrado',
        empty: 'No hay ningún reconocimiento registrado',
        conclusionLabel: 'Conclusión',
        conclusion: {
          fit: 'Apto',
          fit_with_restrictions: 'Apto con restricciones',
          unfit: 'No apto',
          pending: 'Pendiente',
        },
        validUntil: 'Vigente hasta el {date}',
        examinedOn: 'Fecha del reconocimiento',
        provider: 'Servicio de prevención',
        nextReview: 'Próxima revisión',
        certificate: 'Certificado',
        restrictions: 'Restricciones',
        restrictionsHint: 'Qué NO puede hacer. Nunca un diagnóstico ni un motivo médico.',
        noDiagnosis: 'No anotes diagnósticos ni datos clínicos: aquí solo va la conclusión laboral.',
      },

      assignments: {
        empty: 'No está asignado a ninguna obra',
        end: 'Cerrar',
        endTitle: '¿Cerrar la asignación?',
        overridden: 'Autorizado por {who}',
        columns: { where: 'Obra', from: 'Desde', verdict: 'Estado al asignar' },
      },

      assign: {
        title: 'Asignar a una obra',
        where: 'Obra o proyecto',
        wherePlaceholder: 'Buscar o escribir el nombre',
        linked: 'Obra del ERP',
        activities: 'Actividad',
        activitiesHint: 'Si vas a hacer algo concreto, dilo: puede exigir formación propia.',
        anyActivity: 'Sin actividad concreta',
        from: 'Desde',
        override: 'Autorización',
        overrideHint: 'Motivo por el que se asigna igualmente. Queda registrado con tu nombre.',
        submit: 'Asignar',
        done: 'Asignado',
      },

      history: {
        empty: 'Sin cambios registrados',
        now: 'hoy',
        noPosition: 'Sin puesto',
      },

      upload: {
        title: 'Subir un documento',
        type: 'Tipo de documento',
        file: 'Fichero',
        issuedOn: 'Fecha de expedición',
        validUntil: 'Caduca el',
        reference: 'Número o referencia',
        referenceHint: 'El número del carnet, del certificado…',
        hint: 'Si dejas la caducidad vacía se calcula con la vigencia del tipo de documento.',
        submit: 'Subir',
        done: 'Documento subido',
      },

      terminate: {
        title: 'Dar de baja',
        hint: 'Motivo de la baja — queda en el expediente',
      },
      reinstate: { title: '¿Reincorporar a esta persona?' },

      form: {
        newTitle: 'Nuevo expediente',
        newSubtitle: 'La ficha laboral de alguien que ya tiene cuenta en el ERP',
        editTitle: 'Editar el expediente',
        create: 'Abrir expediente',
        created: 'Expediente abierto',
        saved: 'Expediente guardado',
        sectionWho: 'Quién',
        sectionJob: 'Puesto y centro',
        sectionEmployment: 'Situación laboral',
        sectionNotes: 'Observaciones',
        user: 'Persona',
        userHint: 'Toda persona con expediente es un usuario del ERP. Si no está, créala antes en Usuarios.',
        userPlaceholder: 'Buscar en el directorio',
        nameHint: 'El nombre y el correo se cambian en Usuarios, no aquí.',
        sitePlaceholder: 'Centro de trabajo',
        managerPlaceholder: 'Buscar a su responsable',
        probationEndsOn: 'Fin del período de prueba',
        weeklyHours: 'Horas semanales',
        internalNumber: 'Número interno',
        effectiveFrom: 'Con efecto desde',
        effectiveFromHint: 'La fecha en que cambió de verdad, no la de hoy: es lo que queda en el historial.',
        reason: 'Motivo del cambio',
        reasonHint: 'Ascenso, traslado, corrección…',
        notes: 'Observaciones',
        unset: 'Sin especificar',
      },

      matrix: {
        title: 'Matriz de cumplimiento',
        subtitle: 'Trabajadores × requisitos, para el contexto que elijas',
        context: 'Contexto',
        contextHint: 'La misma persona puede estar en regla para una obra y no para otra: aquí se decide la pregunta.',
        customer: 'Cliente',
        anyCustomer: 'Cualquier cliente',
        project: 'Obra',
        anyProject: 'Cualquier obra',
        activity: 'Actividad',
        anyActivity: 'Cualquier actividad',
        apply: 'Aplicar',
        empty: 'No hay nadie que cumpla el filtro',
        emptyHint: 'Prueba a quitar el puesto o el contexto.',
        noColumns: 'No hay requisitos que apliquen',
        noColumnsHint: 'La matriz se dibuja con los requisitos declarados. Todavía no hay ninguno que toque a esta gente.',
        selectAll: 'Seleccionar todos',
      },

      dossier: {
        action: 'Dossier de {count}',
        title: 'Dossier documental',
        selected: '{count} seleccionados',
        compliant: '{count} conformes',
        issues: '{count} con incidencias',
        willCarry: 'Se incluirán {count} documentos.',
        omittedTitle: 'No se incluye',
        omitReason: {
          MISSING: 'no está en el expediente',
          NOT_EXPORTABLE: 'el tipo no es exportable',
          EXPIRED: 'está caducado',
          REJECTED: 'está rechazado',
          PENDING_VALIDATION: 'está pendiente de validar',
          NOT_VALID: 'no está en vigor',
        },
        purpose: 'Para qué',
        purposeHint: 'Queda en el registro de exportaciones: «acceso a la obra de…».',
        generate: 'Generar PDF',
        done: 'Dossier generado',
        company: 'Empresa',
        context: 'Para',
        date: 'Fecha',
        people: 'Personas',
        documents: 'Documentos',
        noDocuments: 'No se incluye ningún documento de esta persona.',
        omitted: 'No incluido',
        footer: 'Generado por General ERP. Los documentos relacionados obran en el expediente y están a disposición de quien los solicite.',
      },

      expiring: {
        title: 'Qué caduca',
        subtitle: 'Papeles y reconocimientos, por fecha',
        window: 'Ventana',
        kind: 'Tipo',
        what: 'Qué',
        on: 'Fecha',
        left: 'Queda',
        empty: 'No vence nada en esta ventana',
        emptyHint: 'Prueba con una ventana más amplia.',
        review: 'Reconocimiento médico',
        overdue: 'hace {days} días',
        inDays: 'en {days} días',
        windows: { expired: 'Ya caducado', days: 'Próximos {days} días' },
        kinds: {
          all: 'Todo',
          document: 'Documento',
          fitness: 'Reconocimiento',
        },
      },

      catalogue: {
        title: 'Requisitos y documentos',
        subtitle: 'Qué papeles lleváis y qué exigís a cada quién',
        tabs: { types: 'Tipos de documento', rules: 'Requisitos' },
        typesHint: 'Los papeles que guardáis de vuestra gente. Son vuestros: renómbralos como los llaméis.',
        rulesHint: 'Qué hace falta, para quién y dónde. Es lo que decide quién sale en rojo.',
        newType: 'Nuevo tipo',
        editType: 'Editar el tipo',
        newRule: 'Nuevo requisito',
        editRule: 'Editar el requisito',
        name: 'Nombre',
        category: 'Categoría',
        categoryHint: 'Sirve para agrupar el dossier y decidir la conservación.',
        categories: {
          identity: 'Identidad',
          employment: 'Relación laboral',
          prevention: 'Prevención',
          qualification: 'Habilitación',
          internal: 'Interno',
          other: 'Otros',
        },
        criticality: 'Criticidad',
        criticalities: { low: 'Baja', normal: 'Normal', critical: 'Crítica' },
        validity: 'Vigencia',
        months: '{count} meses',
        hasExpiry: 'Caduca',
        hasExpiryHint: 'Un DNI caduca; una titulación no.',
        duration: 'Vigencia por defecto (meses)',
        durationHint: 'Con lo que se rellena la caducidad al subir uno.',
        notice: 'Avisos (días)',
        noticeHint: 'Separados por comas. Se avisa una vez en cada uno.',
        requiresValidation: 'Requiere validación',
        requiresValidationHint: 'Hasta que alguien lo dé por bueno, no cuenta como cumplido.',
        requiresFile: 'Requiere fichero',
        requiresFileHint: 'Desactívalo para lo que solo son fechas, sin papel que escanear.',
        exportable: 'Puede salir en un dossier',
        exportableHint: 'Desactivado por defecto: un DNI no es asunto de la constructora que pide el curso de altura.',
        needsValidation: 'Validación',
        critical: 'Crítico',
        notify: 'A quién se avisa',
        notifyHint: 'Cuando se acerca la caducidad.',
        targets: {
          employee: 'Al trabajador',
          manager: 'A su responsable',
          hr: 'A administración',
          prevention: 'A prevención',
        },
        archive: 'Archivar',
        archiveTitle: '¿Archivar «{name}»?',
        noRules: 'Todavía no hay requisitos',
        noRulesHint: 'Sin requisitos, todo el mundo sale como «sin requisitos»: ni bien ni mal, sin saberlo.',
        ruleName: 'Nombre',
        ruleNameHint: 'Lo que se lee cuando no se cumple: «Formación de riesgo eléctrico».',
        proof: 'Qué lo cumple',
        proofKind: 'Se acredita con',
        proofDocument: 'Un documento',
        proofType: 'Documento',
        fitnessProof: 'Reconocimiento médico',
        scope: 'A quién aplica',
        scopeHint: 'Lo que dejes vacío es «cualquiera». Dos casillas rellenas se cumplen a la vez: «los electricistas de este cliente».',
        any: 'Cualquiera',
        everybody: 'Todo el mundo',
        oneCustomer: 'Un cliente',
        oneProject: 'Una obra',
        severity: { blocking: 'Bloquea', warning: 'Solo avisa' },
        severityLabel: 'Efecto',
        severityHint: '«Solo avisa» es una respuesta legítima: un sistema donde todo bloquea acaba con todo desactivado.',
        removeRuleTitle: '¿Eliminar el requisito «{name}»?',
      },

      notify: {
        documentExpiring: {
          title: 'Un papel caduca pronto',
          body: '{document} de {name} caduca en {days} días ({date}).',
        },
        documentExpired: {
          title: 'Un papel ha caducado',
          body: '{document} de {name} caducó hace {days} días ({date}).',
        },
        fitnessExpiring: {
          title: 'Toca reconocimiento médico',
          body: 'A {name} le vence el reconocimiento en {days} días ({date}).',
        },
      },

      related: { customer: 'Personal asignado' },
    },

    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    modules: { personnel: 'Control de accesos' },
    assistant: {
      openers: {
        personnel: {
          'who-can': '¿Quién puede trabajar en una obra?',
          expiring: '¿Qué caduca este mes?',
          'not-in-order': '¿Quién no está en regla?',
        },
      },
    },
    permissions: {
      personnel: {
        read: 'Ver la plantilla y la ficha laboral',
        write: 'Abrir, editar y cerrar expedientes',
        documents: 'Ver los papeles de un expediente',
        documents_manage: 'Subir, renovar y archivar papeles',
        validate: 'Dar por bueno o rechazar un papel',
        fitness_read: 'Ver la aptitud médica (cada consulta se audita)',
        fitness_manage: 'Registrar reconocimientos médicos',
        export: 'Generar dossieres documentales',
        override: 'Asignar a alguien que no está habilitado',
        manage: 'Decidir los requisitos y los tipos de documento',
      },
    },
    settings: {
      ns: {
        personnel: {
          title: 'Expediente de personal',
          subtitle: 'Vuestro vocabulario, a quién se avisa y qué pasa con quien no está habilitado',
          positions: {
            label: 'Los puestos de vuestra plantilla',
            help: 'Vuestros, no nuestros: es la lista sobre la que se declaran los requisitos.',
          },
          categories: {
            label: 'Las categorías profesionales',
            help: 'Tal y como las nombra vuestro convenio.',
          },
          departments: {
            label: 'Los departamentos',
            help: 'Solo si los usáis. Vacío, las fichas no llevan departamento.',
          },
          activities: {
            label: 'Las actividades con riesgo',
            help: 'Es LO que se hace —trabajos en altura, espacios confinados—, no el puesto de quien lo hace.',
          },
          employeeRoles: {
            label: 'Los roles que son plantilla',
            help: 'A quien tenga uno se le abre el expediente al crearle la cuenta.',
          },
          hrRoles: {
            label: 'Los roles de administración de personal',
            help: 'Reciben los avisos de vencimiento.',
          },
          preventionRoles: {
            label: 'Los roles de prevención',
            help: 'Reciben los avisos de reconocimiento médico. Vacío, no llegan a nadie.',
          },
          assignmentPolicy: {
            label: 'Al asignar a alguien que no está habilitado',
            help: 'Avisar, requerir autorización con motivo, o bloquear. Lo normal es avisar.',
          },
          fitnessNoticeDays: {
            label: 'Avisos antes de que caduque un reconocimiento',
            help: 'Se avisa una vez en cada uno.',
          },
          fitnessValidityMonths: {
            label: 'Meses que vale un reconocimiento médico',
            help: 'Solo es el valor con el que se rellena la fecha al registrar uno.',
          },
        },
      },
    },
    audit: {
      actions: {
        personnel: {
          employee_filed: 'Expediente abierto',
          employee_updated: 'Expediente modificado',
          employee_situation_changed: 'Cambio de situación laboral',
          employee_activities_changed: 'Actividades modificadas',
          employee_terminated: 'Baja en la empresa',
          employee_reinstated: 'Reincorporación',
          employee_assigned: 'Asignado a una obra',
          assignment_overridden: 'Asignado sin estar habilitado',
          assignment_ended: 'Asignación cerrada',
          document_filed: 'Documento subido',
          document_renewed: 'Documento renovado',
          document_validated: 'Documento validado',
          document_rejected: 'Documento rechazado',
          document_version_voided: 'Versión anulada',
          document_archived: 'Documento archivado',
          document_restored: 'Documento devuelto al expediente',
          document_type_created: 'Tipo de documento creado',
          document_type_updated: 'Tipo de documento modificado',
          document_type_archived: 'Tipo de documento archivado',
          document_type_restored: 'Tipo de documento devuelto al catálogo',
          requirement_created: 'Requisito creado',
          requirement_updated: 'Requisito modificado',
          requirement_removed: 'Requisito eliminado',
          // Ya no se registran (0.3.0 sacó los contratos del módulo), pero la
          // auditoría de antes los sigue nombrando y tiene que leerse.
          contract_created: 'Contrato creado',
          contract_updated: 'Contrato modificado',
          contract_activated: 'Contrato marcado vigente',
          contract_ended: 'Contrato finalizado',
          contract_cancelled: 'Contrato anulado',
          contract_document_attached: 'Contrato firmado adjuntado',
          fitness_recorded: 'Reconocimiento médico registrado',
          fitness_read: 'Consulta de aptitud médica',
          fitness_history_read: 'Consulta del histórico de aptitud',
          dossier_exported: 'Dossier documental exportado',
        },
      },
    },
  },

  ca: {
    personnel: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        expired: 'un document caducat | {count} documents caducats',
        expiring: 'un document caduca aquesta setmana | {count} documents caduquen aquesta setmana',
      },
      locale: 'ca',
      defaults: {
        positions: {
          electrician: 'Electricista',
          installer: 'Instal·lador',
          welder: 'Soldador',
          mechanic: 'Mecànic',
          operative: 'Operari',
          site_foreman: 'Encarregat d’obra',
          technician: 'Tècnic',
          engineering_office: 'Oficina tècnica',
          administration: 'Administració',
        },
        categories: {
          labourer: 'Peó',
          semi_skilled: 'Especialista',
          skilled: 'Oficial',
          foreman: 'Encarregat',
          technician: 'Tècnic',
          engineer: 'Enginyer',
        },
        activities: {
          work_at_height: 'Treballs en alçada',
          hot_work: 'Treballs en calent',
          electrical_risk: 'Risc elèctric',
          confined_spaces: 'Espais confinats',
          elevating_platform: 'Plataforma elevadora',
          forklift: 'Carretó elevador',
          overhead_crane: 'Pont grua',
          driving: 'Conducció',
        },
      },
      title: 'Control d’accessos',
      subtitle: 'Qui està habilitat per entrar a cada emplaçament o obra, avui',
      detailTitle: 'Expedient',
      notFound: 'No trobem aquest expedient',
      backToList: 'Tornar a la plantilla',
      empty: 'Encara no hi ha expedients',
      emptyHint: 'Un expedient és la fitxa laboral d\'algú: el seu lloc, els seus papers i per a què està habilitat.',
      noName: 'Sense nom',
      more: 'Veure\'n més ({shown} de {total})',
      cancel: 'Cancel·lar',
      save: 'Desar',
      edit: 'Editar',
      remove: 'Eliminar',

      actions: {
        new: 'Nou expedient',
        edit: 'Editar fitxa',
        upload: 'Pujar document',
        assign: 'Assignar a obra',
        matrix: 'Matriu de compliment',
        expiring: 'Què caduca',
        catalogue: 'Requisits i documents',
        dossier: 'Generar dossier',
        terminate: 'Donar de baixa',
        reinstate: 'Reincorporar',
      },

      dashboard: {
        notCompliant: 'No habilitats',
        notCompliantHelp: 'Quanta gent de la plantilla no compleix ara mateix algun requisit obligatori del seu lloc.',
        expiring: 'Documents a punt de caducar',
        expiringHelp: 'Papers de personal que caduquen els propers 30 dies: reconeixements, formació, carnets.',
        expired: 'Documents caducats',
        expiredHelp: 'Papers de personal que ja han caducat i continuen sense renovar.',
        pendingValidation: 'Per validar',
        pendingValidationHelp: 'Documents que algú ha pujat i que encara no ha revisat ningú.',
        soonest: 'El que caduca abans',
        soonestHelp: 'Els papers de personal amb la caducitat més propera, d\'aquí a 60 dies.',
      },

      stats: {
        active: 'En plantilla',
        compliant: 'En regla',
        warning: 'Amb avisos',
        notCompliant: 'No habilitats',
        notConfigured: 'Sense requisits',
      },

      windows: { expired: 'caducats', inDays: 'en {days} dies', pendingValidation: 'per validar' },

      filters: {
        search: 'Cercar',
        searchPlaceholder: 'Nom, codi o número intern',
        position: 'Lloc',
        anyPosition: 'Qualsevol lloc',
        department: 'Departament',
        anyDepartment: 'Qualsevol departament',
        compliance: 'Habilitació',
        anyCompliance: 'Qualsevol estat',
        status: 'Situació',
        anyStatus: 'Tots, altes i baixes',
      },

      columns: {
        name: 'Treballador',
        position: 'Lloc',
        site: 'Centre',
        compliance: 'Habilitació',
        nextExpiry: 'Propera caducitat',
      },

      status: {
        COMPLIANT: 'Habilitat',
        WARNING: 'Amb avisos',
        NON_COMPLIANT: 'No habilitat',
        NOT_CONFIGURED: 'Sense requisits',
      },

      employmentStatus: { active: 'En alta', inactive: 'De baixa' },

      verdict: {
        yes: 'HABILITAT',
        no: 'NO HABILITAT',
        warning: 'HABILITAT AMB AVISOS',
        unknown: 'SENSE REQUISITS CONFIGURATS',
        blocking: '{count} bloquejants',
        askedBy: '· {rule}',
        noRequirements: 'Encara ningú no ha declarat què cal per a aquest lloc.',
        configure: 'Configurar requisits',
        nextExpiry: 'El primer que venç: {date}',
      },

      reasons: {
        DOCUMENT_MISSING: 'Falta {document}',
        DOCUMENT_PENDING_VALIDATION: '{document} està pujat però sense validar',
        DOCUMENT_REJECTED: '{document} es va rebutjar',
        DOCUMENT_EXPIRED: '{document} va caducar fa {days} dies',
        DOCUMENT_EXPIRING: '{document} caduca en {days} dies',
        MEDICAL_FITNESS_MISSING: 'Falta el reconeixement mèdic',
        MEDICAL_FITNESS_PENDING: 'El reconeixement mèdic està pendent',
        MEDICAL_FITNESS_EXPIRED: 'El reconeixement mèdic va caducar fa {days} dies',
        MEDICAL_FITNESS_EXPIRING: 'El reconeixement mèdic venç en {days} dies',
        MEDICAL_FITNESS_UNFIT: 'No apte per al lloc',
        MEDICAL_FITNESS_RESTRICTED: 'Apte amb restriccions',
        MEDICAL_FITNESS_NOT_CLEARED: "El reconeixement mèdic no l'habilita",
        EMPLOYMENT_ENDED: 'Està de baixa a l\'empresa',
      },

      tabs: {
        documents: 'Papers',
        fitness: 'Aptitud',
        assignments: 'Obres',
        history: 'Historial',
      },

      file: {
        title: 'Fitxa laboral',
        code: 'Codi',
        position: 'Lloc',
        category: 'Categoria',
        department: 'Departament',
        site: 'Centre',
        manager: 'Responsable',
        hiredOn: 'Antiguitat',
        agreement: 'Conveni',
        activities: 'Què fa',
        noActivities: 'Sense activitats marcades',
        activitiesHint: 'Les activitats amb risc decideixen quina formació i quins equips se li exigeixen.',
        months: '{count} mesos',
        years: '{count} anys',
        closed: 'Expedient tancat',
        closedOn: 'Baixa el {date}',
      },

      documents: {
        empty: 'Encara no hi ha papers en aquest expedient',
        noExpiry: 'No caduca',
        versions: '{count} versions',
        versionsTitle: 'Versions del document',
        versionsHint: 'Renovar no trepitja la versió anterior: es queda, i així consta amb quin paper es va entrar a una obra.',
        open: 'Obrir',
        validate: 'Validar',
        validated: 'Document validat',
        reject: 'Rebutjar',
        rejectTitle: 'Rebutjar el document',
        rejectHint: 'Motiu — és el que se li dirà al treballador en tornar-l\'hi a demanar',
        renew: 'Renovar',
        void: 'Anul·lar',
        voidTitle: 'Anul·lar la versió',
        voidHint: 'Motiu — la fila es queda com a registre, el fitxer s\'esborra',
        columns: {
          type: 'Document',
          state: 'Estat',
          validUntil: 'Caduca',
          versions: 'Versions',
          filedBy: 'Pujat per',
          validation: 'Validació',
        },
        state: {
          pending: 'Pendent',
          pending_validation: 'Per validar',
          valid: 'Vàlid',
          rejected: 'Rebutjat',
          archived: 'Arxivat',
          expiring: 'Caduca aviat',
          expired: 'Caducat',
        },
        validation: {
          none: 'No cal',
          pending: 'Per validar',
          validated: 'Validat',
          rejected: 'Rebutjat',
        },
      },

      fitness: {
        audited: 'Aquest apartat és confidencial. Cada consulta queda registrada a l\'auditoria.',
        record: 'Registrar reconeixement',
        recorded: 'Reconeixement registrat',
        empty: 'No hi ha cap reconeixement registrat',
        conclusionLabel: 'Conclusió',
        conclusion: {
          fit: 'Apte',
          fit_with_restrictions: 'Apte amb restriccions',
          unfit: 'No apte',
          pending: 'Pendent',
        },
        validUntil: 'Vigent fins al {date}',
        examinedOn: 'Data del reconeixement',
        provider: 'Servei de prevenció',
        nextReview: 'Propera revisió',
        certificate: 'Certificat',
        restrictions: 'Restriccions',
        restrictionsHint: 'Què NO pot fer. Mai un diagnòstic ni un motiu mèdic.',
        noDiagnosis: 'No anotis diagnòstics ni dades clíniques: aquí només hi va la conclusió laboral.',
      },

      assignments: {
        empty: 'No està assignat a cap obra',
        end: 'Tancar',
        endTitle: 'Tancar l\'assignació?',
        overridden: 'Autoritzat per {who}',
        columns: { where: 'Obra', from: 'Des de', verdict: 'Estat en assignar' },
      },

      assign: {
        title: 'Assignar a una obra',
        where: 'Obra o projecte',
        wherePlaceholder: 'Cercar o escriure el nom',
        linked: 'Obra de l\'ERP',
        activities: 'Activitat',
        activitiesHint: 'Si hi fareu alguna cosa concreta, digues-ho: pot exigir formació pròpia.',
        anyActivity: 'Sense activitat concreta',
        from: 'Des de',
        override: 'Autorització',
        overrideHint: 'Motiu pel qual s\'assigna igualment. Queda registrat amb el teu nom.',
        submit: 'Assignar',
        done: 'Assignat',
      },

      history: { empty: 'Sense canvis registrats', now: 'avui', noPosition: 'Sense lloc' },

      upload: {
        title: 'Pujar un document',
        type: 'Tipus de document',
        file: 'Fitxer',
        issuedOn: 'Data d\'expedició',
        validUntil: 'Caduca el',
        reference: 'Número o referència',
        referenceHint: 'El número del carnet, del certificat…',
        hint: 'Si deixes la caducitat buida es calcula amb la vigència del tipus de document.',
        submit: 'Pujar',
        done: 'Document pujat',
      },

      terminate: { title: 'Donar de baixa', hint: 'Motiu de la baixa — queda a l\'expedient' },
      reinstate: { title: 'Reincorporar aquesta persona?' },

      form: {
        newTitle: 'Nou expedient',
        newSubtitle: 'La fitxa laboral d\'algú que ja té compte a l\'ERP',
        editTitle: 'Editar l\'expedient',
        create: 'Obrir expedient',
        created: 'Expedient obert',
        saved: 'Expedient desat',
        sectionWho: 'Qui',
        sectionJob: 'Lloc i centre',
        sectionEmployment: 'Situació laboral',
        sectionNotes: 'Observacions',
        user: 'Persona',
        userHint: 'Tota persona amb expedient és un usuari de l\'ERP. Si no hi és, crea-la abans a Usuaris.',
        userPlaceholder: 'Cercar al directori',
        nameHint: 'El nom i el correu es canvien a Usuaris, no aquí.',
        sitePlaceholder: 'Centre de treball',
        managerPlaceholder: 'Cercar el seu responsable',
        probationEndsOn: 'Fi del període de prova',
        weeklyHours: 'Hores setmanals',
        internalNumber: 'Número intern',
        effectiveFrom: 'Amb efecte des de',
        effectiveFromHint: 'La data en què va canviar de veritat, no la d\'avui: és el que queda a l\'historial.',
        reason: 'Motiu del canvi',
        reasonHint: 'Ascens, trasllat, correcció…',
        notes: 'Observacions',
        unset: 'Sense especificar',
      },

      matrix: {
        title: 'Matriu de compliment',
        subtitle: 'Treballadors × requisits, per al context que triïs',
        context: 'Context',
        contextHint: 'La mateixa persona pot estar en regla per a una obra i no per a una altra: aquí es decideix la pregunta.',
        customer: 'Client',
        anyCustomer: 'Qualsevol client',
        project: 'Obra',
        anyProject: 'Qualsevol obra',
        activity: 'Activitat',
        anyActivity: 'Qualsevol activitat',
        apply: 'Aplicar',
        empty: 'No hi ha ningú que compleixi el filtre',
        emptyHint: 'Prova de treure el lloc o el context.',
        noColumns: 'No hi ha requisits que apliquin',
        noColumnsHint: 'La matriu es dibuixa amb els requisits declarats. Encara no n\'hi ha cap que toqui aquesta gent.',
        selectAll: 'Seleccionar tots',
      },

      dossier: {
        action: 'Dossier de {count}',
        title: 'Dossier documental',
        selected: '{count} seleccionats',
        compliant: '{count} conformes',
        issues: '{count} amb incidències',
        willCarry: 'S\'inclouran {count} documents.',
        omittedTitle: 'No s\'inclou',
        omitReason: {
          MISSING: 'no és a l\'expedient',
          NOT_EXPORTABLE: 'el tipus no és exportable',
          EXPIRED: 'està caducat',
          REJECTED: 'està rebutjat',
          PENDING_VALIDATION: 'està pendent de validar',
          NOT_VALID: 'no és vigent',
        },
        purpose: 'Per a què',
        purposeHint: 'Queda al registre d\'exportacions: «accés a l\'obra de…».',
        generate: 'Generar PDF',
        done: 'Dossier generat',
        company: 'Empresa',
        context: 'Per a',
        date: 'Data',
        people: 'Persones',
        documents: 'Documents',
        noDocuments: 'No s\'inclou cap document d\'aquesta persona.',
        omitted: 'No inclòs',
        footer: 'Generat per General ERP. Els documents relacionats consten a l\'expedient i estan a disposició de qui els sol·liciti.',
      },

      expiring: {
        title: 'Què caduca',
        subtitle: 'Papers i reconeixements, per data',
        window: 'Finestra',
        kind: 'Tipus',
        what: 'Què',
        on: 'Data',
        left: 'Queda',
        empty: 'No venç res en aquesta finestra',
        emptyHint: 'Prova amb una finestra més àmplia.',
        review: 'Reconeixement mèdic',
        overdue: 'fa {days} dies',
        inDays: 'en {days} dies',
        windows: { expired: 'Ja caducat', days: 'Propers {days} dies' },
        kinds: { all: 'Tot', document: 'Document', fitness: 'Reconeixement' },
      },

      catalogue: {
        title: 'Requisits i documents',
        subtitle: 'Quins papers porteu i què exigiu a cadascú',
        tabs: { types: 'Tipus de document', rules: 'Requisits' },
        typesHint: 'Els papers que guardeu de la vostra gent. Són vostres: reanomena\'ls com els digueu.',
        rulesHint: 'Què cal, per a qui i on. És el que decideix qui surt en vermell.',
        newType: 'Nou tipus',
        editType: 'Editar el tipus',
        newRule: 'Nou requisit',
        editRule: 'Editar el requisit',
        name: 'Nom',
        category: 'Categoria',
        categoryHint: 'Serveix per agrupar el dossier i decidir la conservació.',
        categories: {
          identity: 'Identitat',
          employment: 'Relació laboral',
          prevention: 'Prevenció',
          qualification: 'Habilitació',
          internal: 'Intern',
          other: 'Altres',
        },
        criticality: 'Criticitat',
        criticalities: { low: 'Baixa', normal: 'Normal', critical: 'Crítica' },
        validity: 'Vigència',
        months: '{count} mesos',
        hasExpiry: 'Caduca',
        hasExpiryHint: 'Un DNI caduca; una titulació no.',
        duration: 'Vigència per defecte (mesos)',
        durationHint: 'Amb què s\'omple la caducitat en pujar-ne un.',
        notice: 'Avisos (dies)',
        noticeHint: 'Separats per comes. S\'avisa un cop a cadascun.',
        requiresValidation: 'Requereix validació',
        requiresValidationHint: 'Fins que algú no el doni per bo, no compta com a complert.',
        requiresFile: 'Requereix fitxer',
        requiresFileHint: 'Desactiva\'l per al que només són dates, sense paper per escanejar.',
        exportable: 'Pot sortir en un dossier',
        exportableHint: 'Desactivat per defecte: un DNI no és assumpte de la constructora que demana el curs d\'alçada.',
        needsValidation: 'Validació',
        critical: 'Crític',
        notify: 'A qui s\'avisa',
        notifyHint: 'Quan s\'acosta la caducitat.',
        targets: {
          employee: 'Al treballador',
          manager: 'Al seu responsable',
          hr: 'A administració',
          prevention: 'A prevenció',
        },
        archive: 'Arxivar',
        archiveTitle: 'Arxivar «{name}»?',
        noRules: 'Encara no hi ha requisits',
        noRulesHint: 'Sense requisits, tothom surt com a «sense requisits»: ni bé ni malament, sense saber-ho.',
        ruleName: 'Nom',
        ruleNameHint: 'El que es llegeix quan no es compleix: «Formació de risc elèctric».',
        proof: 'Què el compleix',
        proofKind: 'S\'acredita amb',
        proofDocument: 'Un document',
        proofType: 'Document',
        fitnessProof: 'Reconeixement mèdic',
        scope: 'A qui aplica',
        scopeHint: 'El que deixis buit és «qualsevol». Dues caselles omplertes es compleixen alhora: «els electricistes d\'aquest client».',
        any: 'Qualsevol',
        everybody: 'Tothom',
        oneCustomer: 'Un client',
        oneProject: 'Una obra',
        severity: { blocking: 'Bloqueja', warning: 'Només avisa' },
        severityLabel: 'Efecte',
        severityHint: '«Només avisa» és una resposta legítima: un sistema on tot bloqueja acaba amb tot desactivat.',
        removeRuleTitle: 'Eliminar el requisit «{name}»?',
      },

      notify: {
        documentExpiring: {
          title: 'Un paper caduca aviat',
          body: '{document} de {name} caduca en {days} dies ({date}).',
        },
        documentExpired: {
          title: 'Un paper ha caducat',
          body: '{document} de {name} va caducar fa {days} dies ({date}).',
        },
        fitnessExpiring: {
          title: 'Toca reconeixement mèdic',
          body: 'A {name} li venç el reconeixement en {days} dies ({date}).',
        },
      },

      related: { customer: 'Personal assignat' },
    },

    modules: { personnel: 'Control d’accessos' },
    assistant: {
      openers: {
        personnel: {
          'who-can': 'Qui pot treballar en una obra?',
          expiring: 'Què caduca aquest mes?',
          'not-in-order': 'Qui no està en regla?',
        },
      },
    },
    permissions: {
      personnel: {
        read: 'Veure la plantilla i la fitxa laboral',
        write: 'Obrir, editar i tancar expedients',
        documents: 'Veure els papers d\'un expedient',
        documents_manage: 'Pujar, renovar i arxivar papers',
        validate: 'Donar per bo o rebutjar un paper',
        fitness_read: 'Veure l\'aptitud mèdica (cada consulta s\'audita)',
        fitness_manage: 'Registrar reconeixements mèdics',
        export: 'Generar dossiers documentals',
        override: 'Assignar algú que no està habilitat',
        manage: 'Decidir els requisits i els tipus de document',
      },
    },
    settings: {
      ns: {
        personnel: {
          title: 'Expedient de personal',
          subtitle: 'El vostre vocabulari, a qui s\'avisa i què passa amb qui no està habilitat',
          positions: {
            label: 'Els llocs de la vostra plantilla',
            help: 'Vostres, no nostres: és la llista sobre la qual es declaren els requisits.',
          },
          categories: {
            label: 'Les categories professionals',
            help: 'Tal com les anomena el vostre conveni.',
          },
          departments: {
            label: 'Els departaments',
            help: 'Només si els useu. Buit, les fitxes no porten departament.',
          },
          activities: {
            label: 'Les activitats amb risc',
            help: 'És ALLÒ que es fa —treballs en alçada, espais confinats—, no el lloc de qui ho fa.',
          },
          employeeRoles: {
            label: 'Els rols que són plantilla',
            help: 'A qui en tingui un se li obre l\'expedient en crear-li el compte.',
          },
          hrRoles: {
            label: 'Els rols d\'administració de personal',
            help: 'Reben els avisos de venciment.',
          },
          preventionRoles: {
            label: 'Els rols de prevenció',
            help: 'Reben els avisos de reconeixement mèdic. Buit, no arriben a ningú.',
          },
          assignmentPolicy: {
            label: 'En assignar algú que no està habilitat',
            help: 'Avisar, requerir autorització amb motiu, o bloquejar. El normal és avisar.',
          },
          fitnessNoticeDays: {
            label: 'Avisos abans que caduqui un reconeixement',
            help: 'S\'avisa un cop en cadascun.',
          },
          fitnessValidityMonths: {
            label: 'Mesos que val un reconeixement mèdic',
            help: 'Només és el valor amb què s\'omple la data en registrar-ne un.',
          },
        },
      },
    },
    audit: {
      actions: {
        personnel: {
          employee_filed: 'Expedient obert',
          employee_updated: 'Expedient modificat',
          employee_situation_changed: 'Canvi de situació laboral',
          employee_activities_changed: 'Activitats modificades',
          employee_terminated: 'Baixa a l\'empresa',
          employee_reinstated: 'Reincorporació',
          employee_assigned: 'Assignat a una obra',
          assignment_overridden: 'Assignat sense estar habilitat',
          assignment_ended: 'Assignació tancada',
          document_filed: 'Document pujat',
          document_renewed: 'Document renovat',
          document_validated: 'Document validat',
          document_rejected: 'Document rebutjat',
          document_version_voided: 'Versió anul·lada',
          document_archived: 'Document arxivat',
          document_restored: 'Document retornat a l\'expedient',
          document_type_created: 'Tipus de document creat',
          document_type_updated: 'Tipus de document modificat',
          document_type_archived: 'Tipus de document arxivat',
          document_type_restored: 'Tipus de document retornat al catàleg',
          requirement_created: 'Requisit creat',
          requirement_updated: 'Requisit modificat',
          requirement_removed: 'Requisit eliminat',
          contract_created: 'Contracte creat',
          contract_updated: 'Contracte modificat',
          contract_activated: 'Contracte marcat vigent',
          contract_ended: 'Contracte finalitzat',
          contract_cancelled: 'Contracte anul·lat',
          contract_document_attached: 'Contracte signat adjuntat',
          fitness_recorded: 'Reconeixement mèdic registrat',
          fitness_read: 'Consulta d\'aptitud mèdica',
          fitness_history_read: 'Consulta de l\'històric d\'aptitud',
          dossier_exported: 'Dossier documental exportat',
        },
      },
    },
  },

  en: {
    personnel: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        expired: 'one expired document | {count} expired documents',
        expiring: 'one document expires this week | {count} documents expire this week',
      },
      locale: 'en',
      defaults: {
        positions: {
          electrician: 'Electrician',
          installer: 'Installer',
          welder: 'Welder',
          mechanic: 'Mechanic',
          operative: 'Operative',
          site_foreman: 'Site foreman',
          technician: 'Technician',
          engineering_office: 'Engineering office',
          administration: 'Administration',
        },
        categories: {
          labourer: 'Labourer',
          semi_skilled: 'Semi-skilled',
          skilled: 'Skilled',
          foreman: 'Foreman',
          technician: 'Technician',
          engineer: 'Engineer',
        },
        activities: {
          work_at_height: 'Work at height',
          hot_work: 'Hot work',
          electrical_risk: 'Electrical risk',
          confined_spaces: 'Confined spaces',
          elevating_platform: 'Elevating platform',
          forklift: 'Forklift',
          overhead_crane: 'Overhead crane',
          driving: 'Driving',
        },
      },
      title: 'Site access',
      subtitle: 'Who is cleared to go onto each site or job, today',
      detailTitle: 'Personnel file',
      notFound: 'We cannot find this file',
      backToList: 'Back to the staff list',
      empty: 'No files yet',
      emptyHint: 'A file is somebody\'s working record: their job, their papers and what they are cleared for.',
      noName: 'No name',
      more: 'Show more ({shown} of {total})',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      remove: 'Remove',

      actions: {
        new: 'New file',
        edit: 'Edit file',
        upload: 'Upload document',
        assign: 'Assign to a site',
        matrix: 'Compliance matrix',
        expiring: 'What expires',
        catalogue: 'Requirements and documents',
        dossier: 'Generate dossier',
        terminate: 'Close the file',
        reinstate: 'Reinstate',
      },

      dashboard: {
        notCompliant: 'Not eligible',
        notCompliantHelp: 'How many people on the payroll currently fail a requirement their job needs.',
        expiring: 'Papers about to expire',
        expiringHelp: 'Personnel documents expiring in the next 30 days: medicals, training, licences.',
        expired: 'Expired papers',
        expiredHelp: 'Personnel documents that have already expired and have not been renewed.',
        pendingValidation: 'Waiting to be checked',
        pendingValidationHelp: 'Documents somebody uploaded that nobody has reviewed yet.',
        soonest: 'Expiring soonest',
        soonestHelp: 'The personnel papers with the nearest expiry date, within 60 days.',
      },

      stats: {
        active: 'On the books',
        compliant: 'In order',
        warning: 'With warnings',
        notCompliant: 'Not eligible',
        notConfigured: 'No requirements',
      },

      windows: { expired: 'expired', inDays: 'in {days} days', pendingValidation: 'to validate' },

      filters: {
        search: 'Search',
        searchPlaceholder: 'Name, code or internal number',
        position: 'Position',
        anyPosition: 'Any position',
        department: 'Department',
        anyDepartment: 'Any department',
        compliance: 'Eligibility',
        anyCompliance: 'Any state',
        status: 'Employment',
        anyStatus: 'Everybody, past and present',
      },

      columns: {
        name: 'Worker',
        position: 'Position',
        site: 'Site',
        compliance: 'Eligibility',
        nextExpiry: 'Next expiry',
      },

      status: {
        COMPLIANT: 'Eligible',
        WARNING: 'With warnings',
        NON_COMPLIANT: 'Not eligible',
        NOT_CONFIGURED: 'No requirements',
      },

      employmentStatus: { active: 'Employed', inactive: 'Left' },

      verdict: {
        yes: 'ELIGIBLE',
        no: 'NOT ELIGIBLE',
        warning: 'ELIGIBLE, WITH WARNINGS',
        unknown: 'NO REQUIREMENTS CONFIGURED',
        blocking: '{count} blocking',
        askedBy: '· {rule}',
        noRequirements: 'Nobody has said yet what this job needs.',
        configure: 'Set the requirements',
        nextExpiry: 'First to run out: {date}',
      },

      reasons: {
        DOCUMENT_MISSING: '{document} is missing',
        DOCUMENT_PENDING_VALIDATION: '{document} is filed but not accepted yet',
        DOCUMENT_REJECTED: '{document} was refused',
        DOCUMENT_EXPIRED: '{document} expired {days} days ago',
        DOCUMENT_EXPIRING: '{document} expires in {days} days',
        MEDICAL_FITNESS_MISSING: 'No medical examination on file',
        MEDICAL_FITNESS_PENDING: 'The medical examination is pending',
        MEDICAL_FITNESS_EXPIRED: 'The medical examination expired {days} days ago',
        MEDICAL_FITNESS_EXPIRING: 'The medical examination is due in {days} days',
        MEDICAL_FITNESS_UNFIT: 'Not fit for this work',
        MEDICAL_FITNESS_RESTRICTED: 'Fit with restrictions',
        MEDICAL_FITNESS_NOT_CLEARED: 'The medical examination does not clear them',
        EMPLOYMENT_ENDED: 'No longer employed here',
      },

      tabs: {
        documents: 'Papers',
        fitness: 'Fitness',
        assignments: 'Sites',
        history: 'History',
      },

      file: {
        title: 'Working record',
        code: 'Code',
        position: 'Position',
        category: 'Grade',
        department: 'Department',
        site: 'Site',
        manager: 'Reports to',
        hiredOn: 'Since',
        agreement: 'Agreement',
        activities: 'What they do',
        noActivities: 'No activities marked',
        activitiesHint: 'Activities with risk decide what training and equipment are demanded of them.',
        months: '{count} months',
        years: '{count} years',
        closed: 'File closed',
        closedOn: 'Left on {date}',
      },

      documents: {
        empty: 'No papers on this file yet',
        noExpiry: 'Does not expire',
        versions: '{count} versions',
        versionsTitle: 'Versions of the document',
        versionsHint: 'Renewing does not overwrite the previous version: it stays, so it is on record which paper somebody walked onto a site with.',
        open: 'Open',
        validate: 'Accept',
        validated: 'Document accepted',
        reject: 'Refuse',
        rejectTitle: 'Refuse the document',
        rejectHint: 'Reason — it is what the worker is told when they are asked again',
        renew: 'Renew',
        void: 'Void',
        voidTitle: 'Void the version',
        voidHint: 'Reason — the row stays as a record, the file is deleted',
        columns: {
          type: 'Document',
          state: 'State',
          validUntil: 'Expires',
          versions: 'Versions',
          filedBy: 'Filed by',
          validation: 'Validation',
        },
        state: {
          pending: 'Pending',
          pending_validation: 'To validate',
          valid: 'Valid',
          rejected: 'Refused',
          archived: 'Archived',
          expiring: 'Expiring',
          expired: 'Expired',
        },
        validation: {
          none: 'Not needed',
          pending: 'To validate',
          validated: 'Accepted',
          rejected: 'Refused',
        },
      },

      fitness: {
        audited: 'This section is confidential. Every time it is opened is written to the audit log.',
        record: 'Record an examination',
        recorded: 'Examination recorded',
        empty: 'No examination on file',
        conclusionLabel: 'Conclusion',
        conclusion: {
          fit: 'Fit',
          fit_with_restrictions: 'Fit with restrictions',
          unfit: 'Not fit',
          pending: 'Pending',
        },
        validUntil: 'Valid until {date}',
        examinedOn: 'Date of examination',
        provider: 'Prevention service',
        nextReview: 'Next review',
        certificate: 'Certificate',
        restrictions: 'Restrictions',
        restrictionsHint: 'What the person must NOT do. Never a diagnosis or a medical reason.',
        noDiagnosis: 'Do not write diagnoses or clinical data here: only the work conclusion belongs on this screen.',
      },

      assignments: {
        empty: 'Not assigned to any site',
        end: 'Close',
        endTitle: 'Close the assignment?',
        overridden: 'Authorised by {who}',
        columns: { where: 'Site', from: 'From', verdict: 'Verdict when assigned' },
      },

      assign: {
        title: 'Assign to a site',
        where: 'Site or project',
        wherePlaceholder: 'Search or type the name',
        linked: 'Project in the ERP',
        activities: 'Activity',
        activitiesHint: 'If there is a particular kind of work, say so: it may demand its own training.',
        anyActivity: 'No particular activity',
        from: 'From',
        override: 'Authorisation',
        overrideHint: 'Why they are being sent anyway. It is recorded with your name on it.',
        submit: 'Assign',
        done: 'Assigned',
      },

      history: { empty: 'No changes recorded', now: 'today', noPosition: 'No position' },

      upload: {
        title: 'Upload a document',
        type: 'Kind of document',
        file: 'File',
        issuedOn: 'Issued on',
        validUntil: 'Expires on',
        reference: 'Number or reference',
        referenceHint: 'The licence number, the certificate number…',
        hint: 'Leave the expiry empty and it is worked out from the document type\'s validity.',
        submit: 'Upload',
        done: 'Document uploaded',
      },

      terminate: { title: 'Close the file', hint: 'Why they are leaving — it stays on the file' },
      reinstate: { title: 'Reinstate this person?' },

      form: {
        newTitle: 'New file',
        newSubtitle: 'The working record of somebody who already has an ERP account',
        editTitle: 'Edit the file',
        create: 'Open the file',
        created: 'File opened',
        saved: 'File saved',
        sectionWho: 'Who',
        sectionJob: 'Job and site',
        sectionEmployment: 'Employment',
        sectionNotes: 'Notes',
        user: 'Person',
        userHint: 'Everybody with a file is an ERP user. If they are not there yet, create them in Users first.',
        userPlaceholder: 'Search the directory',
        nameHint: 'The name and the email are changed in Users, not here.',
        sitePlaceholder: 'Work centre',
        managerPlaceholder: 'Search for their manager',
        probationEndsOn: 'Trial period ends',
        weeklyHours: 'Weekly hours',
        internalNumber: 'Internal number',
        effectiveFrom: 'Effective from',
        effectiveFromHint: 'The day it actually changed, not today: that is what goes into the history.',
        reason: 'Why it changed',
        reasonHint: 'Promotion, transfer, correction…',
        notes: 'Notes',
        unset: 'Not set',
      },

      matrix: {
        title: 'Compliance matrix',
        subtitle: 'Workers × requirements, for whatever context you choose',
        context: 'Context',
        contextHint: 'The same person can be in order for one site and not for another: this is where the question is set.',
        customer: 'Customer',
        anyCustomer: 'Any customer',
        project: 'Project',
        anyProject: 'Any project',
        activity: 'Activity',
        anyActivity: 'Any activity',
        apply: 'Apply',
        empty: 'Nobody matches the filter',
        emptyHint: 'Try removing the position or the context.',
        noColumns: 'No requirements apply',
        noColumnsHint: 'The matrix is drawn from the requirements declared. None of them reaches these people yet.',
        selectAll: 'Select all',
      },

      dossier: {
        action: 'Dossier of {count}',
        title: 'Document dossier',
        selected: '{count} selected',
        compliant: '{count} in order',
        issues: '{count} with something pending',
        willCarry: '{count} documents will be included.',
        omittedTitle: 'Not included',
        omitReason: {
          MISSING: 'not on file',
          NOT_EXPORTABLE: 'the type may not leave',
          EXPIRED: 'expired',
          REJECTED: 'rejected',
          PENDING_VALIDATION: 'awaiting validation',
          NOT_VALID: 'not in force',
        },
        purpose: 'What for',
        purposeHint: 'It goes into the export log: "access to the site at…".',
        generate: 'Generate PDF',
        done: 'Dossier generated',
        company: 'Company',
        context: 'For',
        date: 'Date',
        people: 'People',
        documents: 'Documents',
        noDocuments: 'No document of this person is included.',
        omitted: 'Not included',
        footer: 'Generated by General ERP. The documents listed are held on file and available on request.',
      },

      expiring: {
        title: 'What expires',
        subtitle: 'Papers and medical reviews, by date',
        window: 'Window',
        kind: 'Kind',
        what: 'What',
        on: 'Date',
        left: 'Left',
        empty: 'Nothing expires in this window',
        emptyHint: 'Try a wider window.',
        review: 'Medical review',
        overdue: '{days} days ago',
        inDays: 'in {days} days',
        windows: { expired: 'Already expired', days: 'Next {days} days' },
        kinds: { all: 'Everything', document: 'Document', fitness: 'Medical' },
      },

      catalogue: {
        title: 'Requirements and documents',
        subtitle: 'What papers you keep, and what you demand of whom',
        tabs: { types: 'Document types', rules: 'Requirements' },
        typesHint: 'The papers you keep about your people. They are yours: rename them to whatever you call them.',
        rulesHint: 'What is needed, of whom and where. This is what decides who comes out red.',
        newType: 'New type',
        editType: 'Edit the type',
        newRule: 'New requirement',
        editRule: 'Edit the requirement',
        name: 'Name',
        category: 'Category',
        categoryHint: 'Used to group a dossier and to decide retention.',
        categories: {
          identity: 'Identity',
          employment: 'Employment',
          prevention: 'Prevention',
          qualification: 'Qualification',
          internal: 'Internal',
          other: 'Other',
        },
        criticality: 'Criticality',
        criticalities: { low: 'Low', normal: 'Normal', critical: 'Critical' },
        validity: 'Validity',
        months: '{count} months',
        hasExpiry: 'Expires',
        hasExpiryHint: 'An ID card expires; a degree does not.',
        duration: 'Default validity (months)',
        durationHint: 'What the expiry box is pre-filled with on upload.',
        notice: 'Warnings (days)',
        noticeHint: 'Comma-separated. One warning at each of them.',
        requiresValidation: 'Needs validation',
        requiresValidationHint: 'Until somebody accepts it, it does not count as met.',
        requiresFile: 'Needs a file',
        requiresFileHint: 'Turn it off for what is only dates, with no paper to scan.',
        exportable: 'May leave in a dossier',
        exportableHint: 'Off by default: an ID card is no business of the contractor asking for the working-at-height card.',
        needsValidation: 'Validation',
        critical: 'Critical',
        notify: 'Who is warned',
        notifyHint: 'When the expiry is coming up.',
        targets: {
          employee: 'The worker',
          manager: 'Their manager',
          hr: 'Administration',
          prevention: 'Prevention',
        },
        archive: 'Archive',
        archiveTitle: 'Archive "{name}"?',
        noRules: 'No requirements yet',
        noRulesHint: 'With no requirements everybody reads as "no requirements": neither right nor wrong, and nobody knows.',
        ruleName: 'Name',
        ruleNameHint: 'What is read when it is not met: "Electrical risk training".',
        proof: 'What satisfies it',
        proofKind: 'Proved by',
        proofDocument: 'A document',
        proofType: 'Document',
        fitnessProof: 'Medical examination',
        scope: 'Who it applies to',
        scopeHint: 'What you leave empty means "anybody". Two boxes filled in apply together: "the electricians of this customer".',
        any: 'Anybody',
        everybody: 'Everybody',
        oneCustomer: 'One customer',
        oneProject: 'One project',
        severity: { blocking: 'Blocks', warning: 'Warns only' },
        severityLabel: 'Effect',
        severityHint: '"Warns only" is a legitimate answer: a system where everything blocks ends up with everything switched off.',
        removeRuleTitle: 'Remove the requirement "{name}"?',
      },

      notify: {
        documentExpiring: {
          title: 'A paper expires soon',
          body: '{document} of {name} expires in {days} days ({date}).',
        },
        documentExpired: {
          title: 'A paper has expired',
          body: '{document} of {name} expired {days} days ago ({date}).',
        },
        fitnessExpiring: {
          title: 'A medical review is due',
          body: '{name}\'s examination runs out in {days} days ({date}).',
        },
      },

      related: { customer: 'Staff assigned' },
    },

    modules: { personnel: 'Site access' },
    assistant: {
      openers: {
        personnel: {
          'who-can': 'Who can work on a given site?',
          expiring: 'What expires this month?',
          'not-in-order': 'Who is not in order?',
        },
      },
    },
    permissions: {
      personnel: {
        read: 'See the staff list and the working record',
        write: 'Open, edit and close files',
        documents: 'See the papers on a file',
        documents_manage: 'File, renew and archive papers',
        validate: 'Accept or refuse a paper',
        fitness_read: 'See medical fitness (every read is audited)',
        fitness_manage: 'Record medical examinations',
        export: 'Generate document dossiers',
        override: 'Assign somebody who is not eligible',
        manage: 'Decide the requirements and the document types',
      },
    },
    settings: {
      ns: {
        personnel: {
          title: 'Personnel files',
          subtitle: 'Your vocabulary, who gets warned, and what happens with somebody who is not eligible',
          positions: {
            label: 'The positions in your company',
            help: 'Comma-separated. Yours, not ours: it is the list the requirements are declared against.',
          },
          categories: {
            label: 'The professional grades',
            help: 'Comma-separated, as your collective agreement names them.',
          },
          departments: {
            label: 'The departments',
            help: 'Comma-separated, and only if you use them. Empty, files carry no department.',
          },
          activities: {
            label: 'The activities with risk',
            help: 'Comma-separated. It is WHAT is done — working at height, confined spaces — not the job title of whoever does it.',
          },
          employeeRoles: {
            label: 'The roles that mean staff',
            help: 'Comma-separated. Anybody with one of them gets a file when their account is created.',
          },
          hrRoles: {
            label: 'The personnel administration roles',
            help: 'Comma-separated. They receive the expiry warnings.',
          },
          preventionRoles: {
            label: 'The prevention roles',
            help: 'Comma-separated. They receive the medical review warnings. Empty, those reach nobody.',
          },
          assignmentPolicy: {
            label: 'Assigning somebody who is not eligible',
            help: 'Warn, require an authorisation with a reason, or block. Warning is the usual answer.',
          },
          fitnessNoticeDays: {
            label: 'Warnings before a medical examination runs out',
            help: 'Each one is announced exactly once.',
          },
          fitnessValidityMonths: {
            label: 'How many months a medical examination lasts',
            help: 'Only the value the date is pre-filled with when one is recorded.',
          },
        },
      },
    },
    audit: {
      actions: {
        personnel: {
          employee_filed: 'File opened',
          employee_updated: 'File edited',
          employee_situation_changed: 'Employment situation changed',
          employee_activities_changed: 'Activities changed',
          employee_terminated: 'Left the company',
          employee_reinstated: 'Reinstated',
          employee_assigned: 'Assigned to a site',
          assignment_overridden: 'Assigned while not eligible',
          assignment_ended: 'Assignment closed',
          document_filed: 'Document filed',
          document_renewed: 'Document renewed',
          document_validated: 'Document accepted',
          document_rejected: 'Document refused',
          document_version_voided: 'Version voided',
          document_archived: 'Document archived',
          document_restored: 'Document brought back into the file',
          document_type_created: 'Document type created',
          document_type_updated: 'Document type edited',
          document_type_archived: 'Document type archived',
          document_type_restored: 'Document type brought back',
          requirement_created: 'Requirement created',
          requirement_updated: 'Requirement edited',
          requirement_removed: 'Requirement removed',
          contract_created: 'Contract created',
          contract_updated: 'Contract edited',
          contract_activated: 'Contract marked in force',
          contract_ended: 'Contract ended',
          contract_cancelled: 'Contract cancelled',
          contract_document_attached: 'Signed contract attached',
          fitness_recorded: 'Medical examination recorded',
          fitness_read: 'Medical fitness read',
          fitness_history_read: 'Medical fitness history read',
          dossier_exported: 'Document dossier exported',
        },
      },
    },
  },

  fr: {
    personnel: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        expired: 'un document expiré | {count} documents expirés',
        expiring: 'un document expire cette semaine | {count} documents expirent cette semaine',
      },
      locale: 'fr',
      defaults: {
        positions: {
          electrician: 'Électricien',
          installer: 'Installateur',
          welder: 'Soudeur',
          mechanic: 'Mécanicien',
          operative: 'Opérateur',
          site_foreman: 'Chef de chantier',
          technician: 'Technicien',
          engineering_office: 'Bureau d’études',
          administration: 'Administration',
        },
        categories: {
          labourer: 'Manœuvre',
          semi_skilled: 'Ouvrier spécialisé',
          skilled: 'Ouvrier qualifié',
          foreman: 'Contremaître',
          technician: 'Technicien',
          engineer: 'Ingénieur',
        },
        activities: {
          work_at_height: 'Travail en hauteur',
          hot_work: 'Travaux par point chaud',
          electrical_risk: 'Risque électrique',
          confined_spaces: 'Espaces confinés',
          elevating_platform: 'Nacelle élévatrice',
          forklift: 'Chariot élévateur',
          overhead_crane: 'Pont roulant',
          driving: 'Conduite',
        },
      },
      title: 'Contrôle des accès',
      subtitle: 'Qui est habilité à entrer sur chaque site ou chantier, aujourd\'hui',
      detailTitle: 'Dossier du salarié',
      notFound: 'Ce dossier est introuvable',
      backToList: 'Retour à l\'effectif',
      empty: 'Aucun dossier pour l\'instant',
      emptyHint: 'Un dossier est la fiche professionnelle de quelqu\'un : son poste, ses papiers et ce pour quoi il est habilité.',
      noName: 'Sans nom',
      more: 'Voir plus ({shown} sur {total})',
      cancel: 'Annuler',
      save: 'Enregistrer',
      edit: 'Modifier',
      remove: 'Supprimer',

      actions: {
        new: 'Nouveau dossier',
        edit: 'Modifier la fiche',
        upload: 'Téléverser un document',
        assign: 'Affecter à un chantier',
        matrix: 'Matrice de conformité',
        expiring: 'Ce qui expire',
        catalogue: 'Exigences et documents',
        dossier: 'Générer un dossier',
        terminate: 'Clôturer le dossier',
        reinstate: 'Réintégrer',
      },

      dashboard: {
        notCompliant: 'Non habilités',
        notCompliantHelp: 'Combien de personnes de l\'effectif ne remplissent pas actuellement une exigence de leur poste.',
        expiring: 'Documents bientôt expirés',
        expiringHelp: 'Documents du personnel qui expirent dans les 30 prochains jours : visites médicales, formations, permis.',
        expired: 'Documents expirés',
        expiredHelp: 'Documents du personnel déjà expirés et toujours pas renouvelés.',
        pendingValidation: 'À valider',
        pendingValidationHelp: 'Documents déposés par quelqu\'un et que personne n\'a encore vérifiés.',
        soonest: 'Ce qui expire en premier',
        soonestHelp: 'Les documents du personnel dont l\'échéance est la plus proche, d\'ici 60 jours.',
      },

      stats: {
        active: 'À l\'effectif',
        compliant: 'En règle',
        warning: 'Avec alertes',
        notCompliant: 'Non habilités',
        notConfigured: 'Sans exigences',
      },

      windows: { expired: 'expirés', inDays: 'dans {days} jours', pendingValidation: 'à valider' },

      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Nom, code ou matricule',
        position: 'Poste',
        anyPosition: 'Tous les postes',
        department: 'Service',
        anyDepartment: 'Tous les services',
        compliance: 'Habilitation',
        anyCompliance: 'Tous les états',
        status: 'Situation',
        anyStatus: 'Tous, présents et sortis',
      },

      columns: {
        name: 'Salarié',
        position: 'Poste',
        site: 'Établissement',
        compliance: 'Habilitation',
        nextExpiry: 'Prochaine échéance',
      },

      status: {
        COMPLIANT: 'Habilité',
        WARNING: 'Avec alertes',
        NON_COMPLIANT: 'Non habilité',
        NOT_CONFIGURED: 'Sans exigences',
      },

      employmentStatus: { active: 'En poste', inactive: 'Sorti' },

      verdict: {
        yes: 'HABILITÉ',
        no: 'NON HABILITÉ',
        warning: 'HABILITÉ AVEC AVERTISSEMENTS',
        unknown: 'AUCUNE EXIGENCE CONFIGURÉE',
        blocking: '{count} bloquantes',
        askedBy: '· {rule}',
        noRequirements: 'Personne n\'a encore indiqué ce qu\'exige ce poste.',
        configure: 'Configurer les exigences',
        nextExpiry: 'La première échéance : {date}',
      },

      reasons: {
        DOCUMENT_MISSING: 'Il manque {document}',
        DOCUMENT_PENDING_VALIDATION: '{document} est déposé mais pas encore validé',
        DOCUMENT_REJECTED: '{document} a été refusé',
        DOCUMENT_EXPIRED: '{document} a expiré il y a {days} jours',
        DOCUMENT_EXPIRING: '{document} expire dans {days} jours',
        MEDICAL_FITNESS_MISSING: 'Pas de visite médicale au dossier',
        MEDICAL_FITNESS_PENDING: 'La visite médicale est en attente',
        MEDICAL_FITNESS_EXPIRED: 'La visite médicale a expiré il y a {days} jours',
        MEDICAL_FITNESS_EXPIRING: 'La visite médicale arrive à échéance dans {days} jours',
        MEDICAL_FITNESS_UNFIT: 'Inapte au poste',
        MEDICAL_FITNESS_RESTRICTED: 'Apte avec restrictions',
        MEDICAL_FITNESS_NOT_CLEARED: "La visite médicale ne l'habilite pas",
        EMPLOYMENT_ENDED: 'A quitté l\'entreprise',
      },

      tabs: {
        documents: 'Papiers',
        fitness: 'Aptitude',
        assignments: 'Chantiers',
        history: 'Historique',
      },

      file: {
        title: 'Fiche professionnelle',
        code: 'Code',
        position: 'Poste',
        category: 'Catégorie',
        department: 'Service',
        site: 'Établissement',
        manager: 'Responsable',
        hiredOn: 'Ancienneté',
        agreement: 'Convention',
        activities: 'Ce qu\'il fait',
        noActivities: 'Aucune activité cochée',
        activitiesHint: 'Les activités à risque décident de la formation et des équipements exigés.',
        months: '{count} mois',
        years: '{count} ans',
        closed: 'Dossier clôturé',
        closedOn: 'Sortie le {date}',
      },

      documents: {
        empty: 'Aucun papier dans ce dossier',
        noExpiry: 'N\'expire pas',
        versions: '{count} versions',
        versionsTitle: 'Versions du document',
        versionsHint: 'Renouveler n\'écrase pas la version précédente : elle reste, et l\'on sait avec quel papier quelqu\'un est entré sur un chantier.',
        open: 'Ouvrir',
        validate: 'Valider',
        validated: 'Document validé',
        reject: 'Refuser',
        rejectTitle: 'Refuser le document',
        rejectHint: 'Motif — c\'est ce que le salarié lira quand on le lui redemandera',
        renew: 'Renouveler',
        void: 'Annuler',
        voidTitle: 'Annuler la version',
        voidHint: 'Motif — la ligne reste comme trace, le fichier est supprimé',
        columns: {
          type: 'Document',
          state: 'État',
          validUntil: 'Expire',
          versions: 'Versions',
          filedBy: 'Déposé par',
          validation: 'Validation',
        },
        state: {
          pending: 'En attente',
          pending_validation: 'À valider',
          valid: 'Valide',
          rejected: 'Refusé',
          archived: 'Archivé',
          expiring: 'Expire bientôt',
          expired: 'Expiré',
        },
        validation: {
          none: 'Non requise',
          pending: 'À valider',
          validated: 'Validé',
          rejected: 'Refusé',
        },
      },

      fitness: {
        audited: 'Cette rubrique est confidentielle. Chaque consultation est inscrite au journal d\'audit.',
        record: 'Enregistrer une visite',
        recorded: 'Visite enregistrée',
        empty: 'Aucune visite enregistrée',
        conclusionLabel: 'Conclusion',
        conclusion: {
          fit: 'Apte',
          fit_with_restrictions: 'Apte avec restrictions',
          unfit: 'Inapte',
          pending: 'En attente',
        },
        validUntil: 'Valable jusqu\'au {date}',
        examinedOn: 'Date de la visite',
        provider: 'Service de prévention',
        nextReview: 'Prochaine visite',
        certificate: 'Certificat',
        restrictions: 'Restrictions',
        restrictionsHint: 'Ce que la personne ne doit PAS faire. Jamais un diagnostic ni un motif médical.',
        noDiagnosis: 'N\'inscrivez ni diagnostic ni donnée clinique : seule la conclusion d\'aptitude a sa place ici.',
      },

      assignments: {
        empty: 'Affecté à aucun chantier',
        end: 'Clore',
        endTitle: 'Clore l\'affectation ?',
        overridden: 'Autorisé par {who}',
        columns: { where: 'Chantier', from: 'Depuis', verdict: 'État à l\'affectation' },
      },

      assign: {
        title: 'Affecter à un chantier',
        where: 'Chantier ou projet',
        wherePlaceholder: 'Rechercher ou saisir le nom',
        linked: 'Chantier de l\'ERP',
        activities: 'Activité',
        activitiesHint: 'S\'il y a un travail particulier, indiquez-le : il peut exiger sa propre formation.',
        anyActivity: 'Aucune activité particulière',
        from: 'Depuis',
        override: 'Autorisation',
        overrideHint: 'Motif de l\'affectation malgré tout. Elle est enregistrée à votre nom.',
        submit: 'Affecter',
        done: 'Affecté',
      },

      history: { empty: 'Aucun changement enregistré', now: 'aujourd\'hui', noPosition: 'Sans poste' },

      upload: {
        title: 'Téléverser un document',
        type: 'Type de document',
        file: 'Fichier',
        issuedOn: 'Date de délivrance',
        validUntil: 'Expire le',
        reference: 'Numéro ou référence',
        referenceHint: 'Le numéro du permis, du certificat…',
        hint: 'Si vous laissez l\'échéance vide, elle est calculée d\'après la validité du type de document.',
        submit: 'Téléverser',
        done: 'Document téléversé',
      },

      terminate: { title: 'Clôturer le dossier', hint: 'Motif du départ — il reste au dossier' },
      reinstate: { title: 'Réintégrer cette personne ?' },

      form: {
        newTitle: 'Nouveau dossier',
        newSubtitle: 'La fiche professionnelle de quelqu\'un qui a déjà un compte dans l\'ERP',
        editTitle: 'Modifier le dossier',
        create: 'Ouvrir le dossier',
        created: 'Dossier ouvert',
        saved: 'Dossier enregistré',
        sectionWho: 'Qui',
        sectionJob: 'Poste et établissement',
        sectionEmployment: 'Situation professionnelle',
        sectionNotes: 'Observations',
        user: 'Personne',
        userHint: 'Toute personne ayant un dossier est un utilisateur de l\'ERP. Créez-la d\'abord dans Utilisateurs.',
        userPlaceholder: 'Rechercher dans l\'annuaire',
        nameHint: 'Le nom et l\'e-mail se modifient dans Utilisateurs, pas ici.',
        sitePlaceholder: 'Établissement',
        managerPlaceholder: 'Rechercher son responsable',
        probationEndsOn: 'Fin de la période d\'essai',
        weeklyHours: 'Heures hebdomadaires',
        internalNumber: 'Matricule',
        effectiveFrom: 'Avec effet au',
        effectiveFromHint: 'La date du changement réel, pas celle du jour : c\'est elle qui figure dans l\'historique.',
        reason: 'Motif du changement',
        reasonHint: 'Promotion, mutation, correction…',
        notes: 'Observations',
        unset: 'Non renseigné',
      },

      matrix: {
        title: 'Matrice de conformité',
        subtitle: 'Salariés × exigences, pour le contexte que vous choisissez',
        context: 'Contexte',
        contextHint: 'La même personne peut être en règle pour un chantier et pas pour un autre : la question se règle ici.',
        customer: 'Client',
        anyCustomer: 'Tous les clients',
        project: 'Chantier',
        anyProject: 'Tous les chantiers',
        activity: 'Activité',
        anyActivity: 'Toutes les activités',
        apply: 'Appliquer',
        empty: 'Personne ne correspond au filtre',
        emptyHint: 'Essayez de retirer le poste ou le contexte.',
        noColumns: 'Aucune exigence ne s\'applique',
        noColumnsHint: 'La matrice se dessine à partir des exigences déclarées. Aucune ne concerne encore ces personnes.',
        selectAll: 'Tout sélectionner',
      },

      dossier: {
        action: 'Dossier de {count}',
        title: 'Dossier documentaire',
        selected: '{count} sélectionnés',
        compliant: '{count} conformes',
        issues: '{count} avec des manques',
        willCarry: '{count} documents seront inclus.',
        omittedTitle: 'Non inclus',
        omitReason: {
          MISSING: 'absent du dossier',
          NOT_EXPORTABLE: 'ce type ne sort pas',
          EXPIRED: 'expiré',
          REJECTED: 'refusé',
          PENDING_VALIDATION: 'en attente de validation',
          NOT_VALID: "n'est pas en vigueur",
        },
        purpose: 'Pour quoi',
        purposeHint: 'Inscrit au journal des exports : « accès au chantier de… ».',
        generate: 'Générer le PDF',
        done: 'Dossier généré',
        company: 'Entreprise',
        context: 'Pour',
        date: 'Date',
        people: 'Personnes',
        documents: 'Documents',
        noDocuments: 'Aucun document de cette personne n\'est inclus.',
        omitted: 'Non inclus',
        footer: 'Généré par General ERP. Les documents listés figurent au dossier et sont disponibles sur demande.',
      },

      expiring: {
        title: 'Ce qui expire',
        subtitle: 'Papiers et visites médicales, par date',
        window: 'Fenêtre',
        kind: 'Type',
        what: 'Quoi',
        on: 'Date',
        left: 'Reste',
        empty: 'Rien n\'expire dans cette fenêtre',
        emptyHint: 'Essayez une fenêtre plus large.',
        review: 'Visite médicale',
        overdue: 'il y a {days} jours',
        inDays: 'dans {days} jours',
        windows: { expired: 'Déjà expiré', days: '{days} prochains jours' },
        kinds: { all: 'Tout', document: 'Document', fitness: 'Visite médicale' },
      },

      catalogue: {
        title: 'Exigences et documents',
        subtitle: 'Les papiers que vous conservez et ce que vous exigez de chacun',
        tabs: { types: 'Types de document', rules: 'Exigences' },
        typesHint: 'Les papiers que vous conservez sur vos salariés. Ils sont à vous : renommez-les comme vous les appelez.',
        rulesHint: 'Ce qu\'il faut, pour qui et où. C\'est ce qui décide qui apparaît en rouge.',
        newType: 'Nouveau type',
        editType: 'Modifier le type',
        newRule: 'Nouvelle exigence',
        editRule: 'Modifier l\'exigence',
        name: 'Nom',
        category: 'Catégorie',
        categoryHint: 'Sert à regrouper le dossier et à décider la conservation.',
        categories: {
          identity: 'Identité',
          employment: 'Relation de travail',
          prevention: 'Prévention',
          qualification: 'Habilitation',
          internal: 'Interne',
          other: 'Autres',
        },
        criticality: 'Criticité',
        criticalities: { low: 'Faible', normal: 'Normale', critical: 'Critique' },
        validity: 'Validité',
        months: '{count} mois',
        hasExpiry: 'Expire',
        hasExpiryHint: 'Une carte d\'identité expire ; un diplôme non.',
        duration: 'Validité par défaut (mois)',
        durationHint: 'Ce qui préremplit l\'échéance au téléversement.',
        notice: 'Alertes (jours)',
        noticeHint: 'Séparées par des virgules. Une alerte à chacune.',
        requiresValidation: 'Nécessite une validation',
        requiresValidationHint: 'Tant que personne ne l\'a validé, il ne compte pas comme satisfait.',
        requiresFile: 'Nécessite un fichier',
        requiresFileHint: 'Désactivez-le pour ce qui n\'est que des dates, sans papier à scanner.',
        exportable: 'Peut figurer dans un dossier',
        exportableHint: 'Désactivé par défaut : une pièce d\'identité ne regarde pas le donneur d\'ordre qui demande le certificat de travail en hauteur.',
        needsValidation: 'Validation',
        critical: 'Critique',
        notify: 'Qui est prévenu',
        notifyHint: 'À l\'approche de l\'échéance.',
        targets: {
          employee: 'Le salarié',
          manager: 'Son responsable',
          hr: 'L\'administration',
          prevention: 'La prévention',
        },
        archive: 'Archiver',
        archiveTitle: 'Archiver « {name} » ?',
        noRules: 'Aucune exigence pour l\'instant',
        noRulesHint: 'Sans exigences, tout le monde ressort « sans exigences » : ni bon ni mauvais, et personne ne le sait.',
        ruleName: 'Nom',
        ruleNameHint: 'Ce qui se lit quand elle n\'est pas satisfaite : « Formation risque électrique ».',
        proof: 'Ce qui la satisfait',
        proofKind: 'Justifiée par',
        proofDocument: 'Un document',
        proofType: 'Document',
        fitnessProof: 'Visite médicale',
        scope: 'À qui elle s\'applique',
        scopeHint: 'Ce que vous laissez vide vaut « n\'importe qui ». Deux cases remplies s\'appliquent ensemble : « les électriciens de ce client ».',
        any: 'N\'importe qui',
        everybody: 'Tout le monde',
        oneCustomer: 'Un client',
        oneProject: 'Un chantier',
        severity: { blocking: 'Bloque', warning: 'Alerte seulement' },
        severityLabel: 'Effet',
        severityHint: '« Alerte seulement » est une réponse légitime : un système où tout bloque finit avec tout désactivé.',
        removeRuleTitle: 'Supprimer l\'exigence « {name} » ?',
      },

      notify: {
        documentExpiring: {
          title: 'Un papier expire bientôt',
          body: '{document} de {name} expire dans {days} jours ({date}).',
        },
        documentExpired: {
          title: 'Un papier a expiré',
          body: '{document} de {name} a expiré il y a {days} jours ({date}).',
        },
        fitnessExpiring: {
          title: 'Visite médicale à prévoir',
          body: 'La visite de {name} arrive à échéance dans {days} jours ({date}).',
        },
      },

      related: { customer: 'Personnel affecté' },
    },

    modules: { personnel: 'Contrôle des accès' },
    assistant: {
      openers: {
        personnel: {
          'who-can': 'Qui peut travailler sur un chantier ?',
          expiring: 'Qu\'est-ce qui expire ce mois-ci ?',
          'not-in-order': 'Qui n\'est pas en règle ?',
        },
      },
    },
    permissions: {
      personnel: {
        read: 'Voir l\'effectif et la fiche professionnelle',
        write: 'Ouvrir, modifier et clôturer des dossiers',
        documents: 'Voir les papiers d\'un dossier',
        documents_manage: 'Déposer, renouveler et archiver des papiers',
        validate: 'Valider ou refuser un papier',
        fitness_read: 'Voir l\'aptitude médicale (chaque consultation est auditée)',
        fitness_manage: 'Enregistrer des visites médicales',
        export: 'Générer des dossiers documentaires',
        override: 'Affecter quelqu\'un qui n\'est pas habilité',
        manage: 'Décider les exigences et les types de document',
      },
    },
    settings: {
      ns: {
        personnel: {
          title: 'Dossiers du personnel',
          subtitle: 'Votre vocabulaire, qui est prévenu, et ce qui se passe avec quelqu\'un de non habilité',
          positions: {
            label: 'Les postes de votre effectif',
            help: 'Les vôtres, pas les nôtres : c\'est la liste sur laquelle les exigences sont déclarées.',
          },
          categories: {
            label: 'Les catégories professionnelles',
            help: 'Telles que votre convention les nomme.',
          },
          departments: {
            label: 'Les services',
            help: 'Seulement si vous en avez. Vide, les fiches n\'en portent pas.',
          },
          activities: {
            label: 'Les activités à risque',
            help: 'C\'est CE QUI est fait — travail en hauteur, espaces confinés —, pas le poste de qui le fait.',
          },
          employeeRoles: {
            label: 'Les rôles qui désignent l\'effectif',
            help: 'Quiconque en a un voit son dossier ouvert à la création du compte.',
          },
          hrRoles: {
            label: 'Les rôles d\'administration du personnel',
            help: 'Ils reçoivent les alertes d\'échéance.',
          },
          preventionRoles: {
            label: 'Les rôles de prévention',
            help: 'Ils reçoivent les alertes de visite médicale. Vide, elles n\'atteignent personne.',
          },
          assignmentPolicy: {
            label: 'Affecter quelqu\'un de non habilité',
            help: 'Alerter, exiger une autorisation motivée, ou bloquer. Alerter est la réponse habituelle.',
          },
          fitnessNoticeDays: {
            label: 'Alertes avant l\'échéance d\'une visite médicale',
            help: 'Chacun n\'est annoncé qu\'une seule fois.',
          },
          fitnessValidityMonths: {
            label: 'Durée de validité d\'une visite médicale (mois)',
            help: 'Seulement la valeur qui préremplit la date lors de l\'enregistrement.',
          },
        },
      },
    },
    audit: {
      actions: {
        personnel: {
          employee_filed: 'Dossier ouvert',
          employee_updated: 'Dossier modifié',
          employee_situation_changed: 'Changement de situation professionnelle',
          employee_activities_changed: 'Activités modifiées',
          employee_terminated: 'Départ de l\'entreprise',
          employee_reinstated: 'Réintégration',
          employee_assigned: 'Affecté à un chantier',
          assignment_overridden: 'Affecté sans être habilité',
          assignment_ended: 'Affectation close',
          document_filed: 'Document déposé',
          document_renewed: 'Document renouvelé',
          document_validated: 'Document validé',
          document_rejected: 'Document refusé',
          document_version_voided: 'Version annulée',
          document_archived: 'Document archivé',
          document_restored: 'Document remis au dossier',
          document_type_created: 'Type de document créé',
          document_type_updated: 'Type de document modifié',
          document_type_archived: 'Type de document archivé',
          document_type_restored: 'Type de document remis au catalogue',
          requirement_created: 'Exigence créée',
          requirement_updated: 'Exigence modifiée',
          requirement_removed: 'Exigence supprimée',
          contract_created: 'Contrat créé',
          contract_updated: 'Contrat modifié',
          contract_activated: 'Contrat marqué en vigueur',
          contract_ended: 'Contrat terminé',
          contract_cancelled: 'Contrat annulé',
          contract_document_attached: 'Contrat signé joint',
          fitness_recorded: 'Visite médicale enregistrée',
          fitness_read: 'Consultation de l\'aptitude médicale',
          fitness_history_read: 'Consultation de l\'historique d\'aptitude',
          dossier_exported: 'Dossier documentaire exporté',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'personnel')))

export default { routes, navigation, messages }
